import { v4, type Manifest } from '@sap/ux-specification-types';
import { QUICKVARPATH } from '@sap/ux-specification-types';
import { descriptionSrcURL, syncRule, validity, hide, enums, message } from '../../../common/decoration/decorators';
import { when, not, opTableContext, and, exists, msg, or } from '../../../common/decoration/decorator-paths';
import { getTableAnalyticalConfigurationPathForOP, getTableSettingsPathForOP } from '../../application';
import {
    exportPersonalizationProperty,
    deletePersonalizationProperty,
    exportPersonalization,
    importEnableMassEdit,
    exportEnableMassEdit,
    exportCreationModeProperty,
    deleteCreationModeProperty,
    exportCreationModeCreationFieldsProperty,
    importCreationModeCreationFieldsProperty,
    importCreationMode
} from './Table';
import type { PopinLayout } from 'sap/m/library';

/**
 * Creation mode configuration for Object Page tables.
 * Uses decorators to control property visibility based on table type and creation mode.
 */
export class TableCreationModeOP implements v4.TableCreationModeOP {
    @syncRule({
        manifest: {
            path: getTableSettingsPathForOP,
            export: exportCreationModeProperty,
            delete: deleteCreationModeProperty
        }
    })
    @descriptionSrcURL('https://ui5.sap.com/#/topic/cfb04f0c58e7409992feb4c91aa9410b')
    @hide(when(opTableContext.type, v4.TableTypeV4.AnalyticalTable))
    @message(
        and(when(opTableContext.type, v4.TableTypeV4.AnalyticalTable), exists(opTableContext.creationMode.name)),
        msg(
            'PROPERTY_NOT_ALLOWED_FOR_TABLE_TYPE',
            { propertyName: 'name', tableType: opTableContext.type },
            { deletable: true }
        )
    )
    @enums(when(opTableContext.type, v4.TableTypeV4.TreeTable), [
        v4.TableCreationModeType.NewPage,
        v4.TableCreationModeType.Inline,
        v4.TableCreationModeType.CreationDialog
    ])
    name?: v4.TableCreationModeType;

    @syncRule({
        manifest: {
            path: getTableSettingsPathForOP,
            export: exportCreationModeProperty,
            delete: deleteCreationModeProperty
        }
    })
    @descriptionSrcURL('https://ui5.sap.com/#/topic/cfb04f0c58e7409992feb4c91aa9410b')
    @hide(
        or(
            when(opTableContext.type, v4.TableTypeV4.AnalyticalTable),
            when(opTableContext.type, v4.TableTypeV4.TreeTable),
            and(
                exists(opTableContext.creationMode.name),
                not(opTableContext.creationMode.name, v4.TableCreationModeType.Inline)
            ),
            // When name is not set, navigation determines default: NewPage if navigation exists, Inline otherwise
            and(not(exists(opTableContext.creationMode.name)), when(opTableContext.hasNavigation, true))
        )
    )
    @message(
        and(when(opTableContext.type, v4.TableTypeV4.AnalyticalTable), exists(opTableContext.creationMode.createAtEnd)),
        msg(
            'PROPERTY_NOT_ALLOWED_FOR_TABLE_TYPE',
            { propertyName: 'createAtEnd', tableType: opTableContext.type },
            { deletable: true }
        )
    )
    @message(
        and(when(opTableContext.type, v4.TableTypeV4.TreeTable), exists(opTableContext.creationMode.createAtEnd)),
        msg(
            'PROPERTY_NOT_ALLOWED_FOR_TABLE_TYPE',
            { propertyName: 'createAtEnd', tableType: v4.TableTypeV4.TreeTable },
            { deletable: true }
        )
    )
    @message(
        and(
            exists(opTableContext.creationMode.name),
            not(opTableContext.creationMode.name, v4.TableCreationModeType.Inline),
            exists(opTableContext.creationMode.createAtEnd)
        ),
        msg(
            'PROPERTY_ONLY_ALLOWED_FOR_CREATION_MODE',
            { propertyName: 'createAtEnd', creationMode: v4.TableCreationModeType.Inline },
            { deletable: true }
        )
    )
    @message(
        and(
            // When name is not set, default creation mode is determined by navigation (NewPage if navigation exists)
            not(exists(opTableContext.creationMode.name)),
            when(opTableContext.hasNavigation, true),
            exists(opTableContext.creationMode.createAtEnd)
        ),
        msg(
            'PROPERTY_ONLY_ALLOWED_FOR_CREATION_MODE',
            { propertyName: 'createAtEnd', creationMode: v4.TableCreationModeType.Inline },
            { deletable: true }
        )
    )
    createAtEnd?: boolean;

    @syncRule({
        manifest: {
            path: getTableSettingsPathForOP,
            export: exportCreationModeProperty,
            delete: deleteCreationModeProperty
        }
    })
    @descriptionSrcURL('https://ui5.sap.com/#/topic/cfb04f0c58e7409992feb4c91aa9410b')
    @hide(
        or(
            when(opTableContext.type, v4.TableTypeV4.AnalyticalTable),
            not(opTableContext.creationMode.name, v4.TableCreationModeType.InlineCreationRows),
            not(opTableContext.type, v4.TableTypeV4.ResponsiveTable)
        )
    )
    @message(
        and(
            when(opTableContext.type, v4.TableTypeV4.AnalyticalTable),
            exists(opTableContext.creationMode.inlineCreationRowsHiddenInEditMode)
        ),
        msg(
            'PROPERTY_NOT_ALLOWED_FOR_TABLE_TYPE',
            { propertyName: 'inlineCreationRowsHiddenInEditMode', tableType: opTableContext.type },
            { deletable: true }
        )
    )
    @message(
        and(
            not(opTableContext.type, v4.TableTypeV4.ResponsiveTable),
            exists(opTableContext.creationMode.inlineCreationRowsHiddenInEditMode)
        ),
        msg(
            'PROPERTY_ONLY_ALLOWED_FOR_TABLE_TYPE',
            { propertyName: 'inlineCreationRowsHiddenInEditMode', tableType: v4.TableTypeV4.ResponsiveTable },
            { deletable: true }
        )
    )
    @message(
        and(
            not(opTableContext.creationMode.name, v4.TableCreationModeType.InlineCreationRows),
            exists(opTableContext.creationMode.inlineCreationRowsHiddenInEditMode)
        ),
        msg(
            'PROPERTY_ONLY_ALLOWED_FOR_CREATION_MODE',
            {
                propertyName: 'inlineCreationRowsHiddenInEditMode',
                creationMode: v4.TableCreationModeType.InlineCreationRows
            },
            { deletable: true }
        )
    )
    inlineCreationRowsHiddenInEditMode?: boolean;

    @syncRule({
        manifest: {
            path: getTableSettingsPathForOP,
            export: exportCreationModeProperty,
            delete: deleteCreationModeProperty
        }
    })
    @descriptionSrcURL('https://ui5.sap.com/#/topic/7cf7a31fd1ee490ab816ecd941bd2f1f')
    @hide(
        or(
            when(opTableContext.type, v4.TableTypeV4.AnalyticalTable),
            not(opTableContext.type, v4.TableTypeV4.TreeTable)
        )
    )
    @message(
        and(
            when(opTableContext.type, v4.TableTypeV4.AnalyticalTable),
            exists(opTableContext.creationMode.createInPlace)
        ),
        msg(
            'PROPERTY_NOT_ALLOWED_FOR_TABLE_TYPE',
            { propertyName: 'createInPlace', tableType: opTableContext.type },
            { deletable: true }
        )
    )
    @message(
        and(not(opTableContext.type, v4.TableTypeV4.TreeTable), exists(opTableContext.creationMode.createInPlace)),
        msg(
            'PROPERTY_ONLY_ALLOWED_FOR_TABLE_TYPE',
            { propertyName: 'createInPlace', tableType: v4.TableTypeV4.TreeTable },
            { deletable: true }
        )
    )
    createInPlace?: boolean;

    @syncRule({
        manifest: {
            path: getTableSettingsPathForOP,
            export: exportCreationModeProperty,
            delete: deleteCreationModeProperty
        }
    })
    @descriptionSrcURL('https://ui5.sap.com/#/topic/7cf7a31fd1ee490ab816ecd941bd2f1f')
    @hide(
        or(
            when(opTableContext.type, v4.TableTypeV4.AnalyticalTable),
            not(opTableContext.type, v4.TableTypeV4.TreeTable)
        )
    )
    @message(
        and(
            when(opTableContext.type, v4.TableTypeV4.AnalyticalTable),
            exists(opTableContext.creationMode.isCreateEnabled)
        ),
        msg(
            'PROPERTY_NOT_ALLOWED_FOR_TABLE_TYPE',
            { propertyName: 'isCreateEnabled', tableType: opTableContext.type },
            { deletable: true }
        )
    )
    @message(
        and(not(opTableContext.type, v4.TableTypeV4.TreeTable), exists(opTableContext.creationMode.isCreateEnabled)),
        msg(
            'PROPERTY_ONLY_ALLOWED_FOR_TABLE_TYPE',
            { propertyName: 'isCreateEnabled', tableType: v4.TableTypeV4.TreeTable },
            { deletable: true }
        )
    )
    isCreateEnabled?: string;

    @syncRule({
        manifest: {
            path: getTableSettingsPathForOP,
            export: exportCreationModeCreationFieldsProperty,
            import: importCreationModeCreationFieldsProperty
        }
    })
    @descriptionSrcURL('https://ui5.sap.com/#/topic/7fee938d7cba4bfd86810cfe6d011eb0')
    @hide(not(opTableContext.creationMode.name, v4.TableCreationModeType.CreationDialog))
    @message(
        and(
            when(opTableContext.type, v4.TableTypeV4.AnalyticalTable),
            exists(opTableContext.creationMode.creationFields)
        ),
        msg(
            'PROPERTY_NOT_ALLOWED_FOR_TABLE_TYPE',
            { propertyName: 'creationFields', tableType: opTableContext.type },
            { deletable: true }
        )
    )
    @message(
        and(
            not(opTableContext.creationMode.name, v4.TableCreationModeType.CreationDialog),
            exists(opTableContext.creationMode.creationFields)
        ),
        msg(
            'PROPERTY_ONLY_ALLOWED_FOR_CREATION_MODE',
            { propertyName: 'creationFields', creationMode: v4.TableCreationModeType.CreationDialog },
            { deletable: true }
        )
    )
    creationFields?: string[];

    @syncRule({
        manifest: {
            path: getTableSettingsPathForOP,
            export: exportCreationModeProperty,
            delete: deleteCreationModeProperty
        }
    })
    @descriptionSrcURL('https://ui5.sap.com/#/topic/5c2bc2ea8a7e482e968124959354d736')
    @hide(
        or(
            when(opTableContext.type, v4.TableTypeV4.AnalyticalTable),
            not(opTableContext.creationMode.name, v4.TableCreationModeType.External)
        )
    )
    @message(
        and(when(opTableContext.type, v4.TableTypeV4.AnalyticalTable), exists(opTableContext.creationMode.outbound)),
        msg(
            'PROPERTY_NOT_ALLOWED_FOR_TABLE_TYPE',
            { propertyName: 'outbound', tableType: opTableContext.type },
            { deletable: true }
        )
    )
    @message(
        and(
            not(opTableContext.creationMode.name, v4.TableCreationModeType.External),
            exists(opTableContext.creationMode.outbound)
        ),
        msg(
            'PROPERTY_ONLY_ALLOWED_FOR_CREATION_MODE',
            { propertyName: 'outbound', creationMode: v4.TableCreationModeType.External },
            { deletable: true }
        )
    )
    outbound?: string;
}

export class PersonalizationTypeOP implements v4.PersonalizationTypeOP {
    @syncRule({
        manifest: {
            path: getTableSettingsPathForOP, // Here we are using path of parent to have correct value for manifestSection
            export: exportPersonalizationProperty,
            delete: deletePersonalizationProperty
        }
    })
    column?: boolean;

    @syncRule({
        manifest: {
            path: getTableSettingsPathForOP, // Here we are using path of parent to have correct value for manifestSection
            export: exportPersonalizationProperty,
            delete: deletePersonalizationProperty
        }
    })
    sort?: boolean;

    @syncRule({
        manifest: {
            path: getTableSettingsPathForOP,
            export: exportPersonalizationProperty,
            delete: deletePersonalizationProperty
        }
    })
    filter?: boolean;
}

export class QuickVariantSelectionV4OP implements v4.QuickVariantSelectionV4 {
    @syncRule({
        manifest: {
            path: (pageName: string[], manifest: Manifest, sectionId: string): string =>
                getTableSettingsPathForOP(pageName, manifest, sectionId) + QUICKVARPATH
        }
    })
    paths: v4.AnnotationPathAsObject[];

    @syncRule({
        manifest: {
            path: (pageName: string[], manifest: Manifest, sectionId: string): string =>
                getTableSettingsPathForOP(pageName, manifest, sectionId) + QUICKVARPATH
        }
    })
    hideTableTitle?: boolean;

    @syncRule({
        manifest: {
            path: (pageName: string[], manifest: Manifest, sectionId: string): string =>
                getTableSettingsPathForOP(pageName, manifest, sectionId) + QUICKVARPATH
        }
    })
    showCounts?: boolean;
}

export class ObjectPageTable implements v4.ObjectPageTable {
    @syncRule({
        manifest: {
            path: getTableSettingsPathForOP
        }
    })
    @validity({
        since: '1.84.0'
    })
    type?: v4.TableTypeV4;

    @syncRule({
        manifest: {
            path: getTableSettingsPathForOP,
            export: exportPersonalization
        }
    })
    @validity({
        since: '1.86.0'
    })
    personalization?: PersonalizationTypeOP | boolean;

    @syncRule({
        manifest: {
            path: getTableSettingsPathForOP
        }
    })
    @validity({
        since: '1.84.0'
    })
    selectionMode?: v4.SelectionMode;

    @syncRule({
        manifest: {
            path: getTableSettingsPathForOP
        }
    })
    @validity({
        since: '1.84.0'
    })
    selectAll?: boolean;

    @syncRule({
        manifest: {
            path: getTableSettingsPathForOP
        }
    })
    @validity({
        since: '1.84.0'
    })
    selectionLimit?: number;

    @syncRule({
        manifest: {
            path: getTableSettingsPathForOP
        }
    })
    @validity({
        since: '1.84.0'
    })
    @descriptionSrcURL('https://ui5.sap.com/sdk/#/topic/4bab6f2043814257974b52d4dafe1dcd')
    enableExport?: boolean;

    @syncRule({
        manifest: {
            path: getTableSettingsPathForOP
        }
    })
    @validity({
        since: '1.84.0'
    })
    quickVariantSelection?: QuickVariantSelectionV4OP;

    @syncRule({
        manifest: {
            path: getTableSettingsPathForOP
        }
    })
    @validity({
        since: '1.84.0'
    })
    @descriptionSrcURL('https://ui5.sap.com/sdk/#/topic/f3cc057e405c4fd58ee2ed42c557797c')
    condensedTableLayout?: boolean;

    @syncRule({
        manifest: {
            path: getTableSettingsPathForOP
        }
    })
    @validity({
        since: '1.86.0'
    })
    enableFullScreen?: boolean;

    @syncRule({
        manifest: {
            path: getTableSettingsPathForOP
        }
    })
    @validity({
        since: '1.84.0'
    })
    enablePaste?: boolean;

    @syncRule({
        manifest: {
            path: getTableSettingsPathForOP
        }
    })
    @validity({
        since: '1.117.0'
    })
    @descriptionSrcURL('https://ui5.sap.com/#/topic/7f844f1021cd4791b8f7408eac7c1cec')
    hierarchyQualifier?: string;

    @syncRule({
        manifest: {
            path: getTableSettingsPathForOP
        }
    })
    @validity({
        since: '1.120.0'
    })
    @descriptionSrcURL('https://ui5.sap.com/#/topic/c0f6592a592e47f9bb6d09900de47412')
    widthIncludingColumnHeader?: boolean;

    @syncRule({
        manifest: {
            path: getTableSettingsPathForOP
        }
    })
    @validity({
        since: '1.123.0'
    })
    @descriptionSrcURL('https://ui5.sap.com/#/topic/c0f6592a592e47f9bb6d09900de47412')
    disableCopyToClipboard?: boolean;

    @syncRule({
        manifest: {
            path: getTableSettingsPathForOP,
            import: importEnableMassEdit,
            export: exportEnableMassEdit
        }
    })
    @validity({
        since: '1.126.0'
    })
    enableMassEdit?: v4.EnableMassEdit;

    @syncRule({
        manifest: {
            path: getTableSettingsPathForOP,
            import: importCreationMode
        }
    })
    @descriptionSrcURL('https://ui5.sap.com/#/api/sap.fe.macros.Table%23controlProperties')
    @validity({
        since: '1.130.0'
    })
    @hide(
        or(
            and(when(opTableContext.type, v4.TableTypeV4.AnalyticalTable), not(exists(opTableContext.creationMode))),
            when(opTableContext.isInsertable, false)
        )
    )
    creationMode?: TableCreationModeOP;

    @syncRule({
        manifest: {
            path: getTableSettingsPathForOP
        }
    })
    @descriptionSrcURL('https://ui5.sap.com/#/api/sap.ui.mdc.enums.TableRowCountMode')
    @validity({
        since: '1.120.0'
        /**
         * since '1.130.0': 'Interactive', special message in sync/v4/generate/objectPage.ts
         */
    })
    @hide(when(opTableContext.type, v4.TableTypeV4.ResponsiveTable))
    @message(
        and(when(opTableContext.type, v4.TableTypeV4.ResponsiveTable), exists(opTableContext.rowCountMode)),
        msg(
            'PROPERTY_NOT_ALLOWED_FOR_TABLE_TYPE',
            { propertyName: 'rowCountMode', tableType: v4.TableTypeV4.ResponsiveTable },
            { deletable: true }
        )
    )
    @enums(and(when(opTableContext.sectionLayout, v4.SectionLayoutType.Tabs), not(opTableContext.isCollection, true)), [
        v4.RowCountMode.Auto
    ])
    @message(
        and(
            when(opTableContext.sectionLayout, v4.SectionLayoutType.Tabs),
            not(opTableContext.isCollection, true),
            exists(opTableContext.rowCountMode),
            not(opTableContext.rowCountMode, v4.RowCountMode.Auto)
        ),
        msg('WARNING_DEFAULT_ROW_COUNT_MODE')
    )
    rowCountMode?: v4.RowCountMode;

    @syncRule({
        manifest: {
            path: getTableSettingsPathForOP
        }
    })
    @validity({
        since: '1.120.0'
    })
    @hide(when(opTableContext.type, v4.TableTypeV4.ResponsiveTable))
    @message(
        and(when(opTableContext.type, v4.TableTypeV4.ResponsiveTable), exists(opTableContext.rowCount)),
        msg(
            'PROPERTY_NOT_ALLOWED_FOR_TABLE_TYPE',
            { propertyName: 'rowCount', tableType: v4.TableTypeV4.ResponsiveTable },
            { deletable: true }
        )
    )
    rowCount?: number;

    @syncRule({
        manifest: {
            path: getTableSettingsPathForOP
        }
    })
    @validity({
        since: '1.139.0'
    })
    @hide(not(opTableContext.type, v4.TableTypeV4.ResponsiveTable))
    @message(
        and(not(opTableContext.type, v4.TableTypeV4.ResponsiveTable), exists(opTableContext.popinLayout)),
        msg(
            'PROPERTY_ONLY_ALLOWED_FOR_TABLE_TYPE',
            { propertyName: 'popinLayout', tableType: v4.TableTypeV4.ResponsiveTable },
            { deletable: true }
        )
    )
    popinLayout?: PopinLayout;

    @syncRule({
        manifest: {
            path: getTableSettingsPathForOP
        }
    })
    @validity({
        since: '1.136.0'
    })
    @hide(not(opTableContext.type, v4.TableTypeV4.AnalyticalTable))
    @message(
        and(not(opTableContext.type, v4.TableTypeV4.AnalyticalTable), exists(opTableContext.analyticalConfiguration)),
        msg(
            'PROPERTY_ONLY_ALLOWED_FOR_TABLE_TYPE',
            { propertyName: 'analyticalConfiguration', tableType: v4.TableTypeV4.AnalyticalTable },
            { deletable: true }
        )
    )
    analyticalConfiguration?: ObjectPageAnalyticalConfiguration;
}

export class ObjectPageAnalyticalConfiguration implements v4.ObjectPageAnalyticalConfiguration {
    @syncRule({
        manifest: {
            path: getTableAnalyticalConfigurationPathForOP
        }
    })
    @validity({
        since: '1.136.0'
    })
    aggregationOnLeafLevel?: boolean;
}
